import round2Decimals from "../Components/round2Decimals";

/**
 * Espejo en el frontend del motor de reglas de listas de precios.
 *
 * El original vive en erp_backend/Utils/reglasPrecio.utils.js y es la fuente de
 * verdad: el backend es quien bloquea. Este archivo existe para que la captura
 * de ventas pueda avisar en vivo, sin ir al servidor en cada tecla.
 * Si se cambia la aritmetica de uno, hay que cambiarla en el otro.
 *
 * Solo se replican las funciones de lectura/validacion. El recalculo de precios
 * del catalogo (recalcularPreciosDeInsumo) es exclusivo del backend.
 */

// Alcances de ReglaPrecio
export const ALCANCE = {
  LISTA: 1,
  TIPO_CLIENTE: 2,
  CLIENTE: 3,
};

// Tri-estado de las banderas por lista: sin valor = heredar de la empresa
export const TRIESTADO = {
  HEREDAR: 0,
  SI: 1,
  NO: 2,
};

// De donde sale el margen con el que se recalcula el precio al cambiar el costo
export const BASE_RECALCULO = {
  MARGEN_ACTUAL: 0,
  MARGEN_FIJO: 1,
  MARGEN_LISTA: 2,
};

export const OPCIONES_BASE_RECALCULO = [
  { valor: 0, etiqueta: "Margen actual del insumo" },
  { valor: 1, etiqueta: "Margen fijo del insumo" },
  { valor: 2, etiqueta: "Margen de la lista" },
];

export const ETIQUETAS_BASE_RECALCULO = OPCIONES_BASE_RECALCULO.reduce((acc, o) => {
  acc[o.valor] = o.etiqueta;
  return acc;
}, {});

// Modos de redondeo comercial
export const REDONDEO = {
  NINGUNO: 0,
  DOS_DECIMALES: 1,
  ENTERO: 2,
  MULTIPLO_5: 3,
  TERMINAR_99: 4,
  TERMINAR_50: 5,
};

export const OPCIONES_REDONDEO = [
  { valor: 0, etiqueta: "Ninguno" },
  { valor: 1, etiqueta: "A 2 decimales" },
  { valor: 2, etiqueta: "Al entero" },
  { valor: 3, etiqueta: "A multiplo de 5" },
  { valor: 4, etiqueta: "Terminar en .99" },
  { valor: 5, etiqueta: "Terminar en .50" },
];

export const OPCIONES_TRIESTADO = [
  { valor: 0, etiqueta: "Heredar de la empresa" },
  { valor: 1, etiqueta: "Si" },
  { valor: 2, etiqueta: "No" },
];

export const OPCIONES_ALCANCE = [
  { valor: 1, etiqueta: "Lista base" },
  { valor: 2, etiqueta: "Tipo de cliente" },
  { valor: 3, etiqueta: "Cliente" },
];

export const ETIQUETAS_REDONDEO = OPCIONES_REDONDEO.reduce((acc, o) => {
  acc[o.valor] = o.etiqueta;
  return acc;
}, {});

export const ETIQUETAS_TRIESTADO = {
  0: "Heredado",
  1: "Si",
  2: "No",
};

export const ETIQUETAS_ALCANCE = OPCIONES_ALCANCE.reduce((acc, o) => {
  acc[o.valor] = o.etiqueta;
  return acc;
}, {});

const esNumero = (valor) =>
  valor !== null && valor !== undefined && valor !== "" && !isNaN(Number(valor));

// Atajos de la calculadora de precios que se usan cuando la empresa no configuro
// los suyos (Configuracion > Insumos). Son los que el modal traia hardcodeados.
export const ATAJOS_PORCENTAJE_DEFAULT = [10, 20, 30, 50];

/**
 * Convierte Empresa.atajosPorcentaje ([{ porcentaje }]) en un arreglo de numeros
 * listo para pintar. Arreglo vacio o invalido -> los atajos por defecto.
 *
 * Se filtra con esNumero y no con Number.isFinite(Number(x)): antd manda null al
 * limpiar un InputNumber, y Number(null) es 0, o sea que un renglon vacio se
 * colaria como un atajo de "+0%".
 *
 * NO ordena: respeta el orden en que la empresa los capturo.
 */
export const normalizarAtajosPorcentaje = (atajos) => {
  const limpios = (Array.isArray(atajos) ? atajos : [])
    .filter((atajo) => esNumero(atajo?.porcentaje))
    .map((atajo) => Number(atajo.porcentaje));
  return limpios.length ? limpios : ATAJOS_PORCENTAJE_DEFAULT;
};

const mismoId = (a, b) => {
  if (!a || !b) return false;
  return a.toString() === b.toString();
};

export const resolverTriestado = (valorLista, valorEmpresa) => {
  const numero = Number(valorLista);
  if (numero === TRIESTADO.SI) return true;
  if (numero === TRIESTADO.NO) return false;
  return !!valorEmpresa;
};

export const precioDesdeMargen = (costo, porcentaje, calcularPreciosSobre) => {
  if (!esNumero(costo) || !esNumero(porcentaje)) return null;
  const costoNumero = Number(costo);
  const porcentajeNumero = Number(porcentaje);

  if (calcularPreciosSobre) {
    return round2Decimals((costoNumero * (100 + porcentajeNumero)) / 100);
  }

  const denominador = (100 - porcentajeNumero) / 100;
  if (denominador <= 0) return null;
  return round2Decimals(costoNumero / denominador);
};

export const margenDesdePrecio = (precio, costo, calcularPreciosSobre) => {
  if (!esNumero(precio) || !esNumero(costo)) return null;
  const precioNumero = Number(precio);
  const costoNumero = Number(costo);

  if (calcularPreciosSobre) {
    if (costoNumero === 0) return null;
    return round2Decimals(((precioNumero - costoNumero) / costoNumero) * 100);
  }

  if (precioNumero === 0) return null;
  return round2Decimals(((precioNumero - costoNumero) / precioNumero) * 100);
};

export const clampMargen = (porcentaje, regla) => {
  if (!esNumero(porcentaje)) return porcentaje;
  let valor = Number(porcentaje);
  if (!regla) return valor;

  if (esNumero(regla.margenMinimo) && valor < Number(regla.margenMinimo)) {
    valor = Number(regla.margenMinimo);
  }
  if (esNumero(regla.margenMaximo) && valor > Number(regla.margenMaximo)) {
    valor = Number(regla.margenMaximo);
  }
  return valor;
};

export const descuentoMaximoAplicable = (regla, cantidad) => {
  if (!regla) return undefined;

  if (Array.isArray(regla.escalas) && regla.escalas.length && esNumero(cantidad)) {
    const cantidadNumero = Number(cantidad);
    const escala = regla.escalas.find((valueEscala) => {
      if (!esNumero(valueEscala?.cantidadDesde)) return false;
      if (cantidadNumero < Number(valueEscala.cantidadDesde)) return false;
      if (
        esNumero(valueEscala.cantidadHasta) &&
        cantidadNumero > Number(valueEscala.cantidadHasta)
      ) {
        return false;
      }
      return true;
    });
    if (escala && esNumero(escala.descuentoMaximo)) return Number(escala.descuentoMaximo);
  }

  return esNumero(regla.descuentoMaximo) ? Number(regla.descuentoMaximo) : undefined;
};

export const descuentoEfectivo = ({
  precioUnitario,
  cantidad,
  descuento,
  descuentoCantidad,
}) => {
  const precioNumero = Number(precioUnitario) || 0;
  const cantidadNumero = Number(cantidad) || 0;
  const subtotal = precioNumero * cantidadNumero;
  if (!subtotal) return 0;

  const totalDescuento =
    subtotal * ((Number(descuento) || 0) / 100) + (Number(descuentoCantidad) || 0);
  return round2Decimals((totalDescuento / subtotal) * 100);
};

export const margenEfectivo = ({
  precioUnitario,
  cantidad,
  descuento,
  descuentoCantidad,
  costo,
  calcularPreciosSobre,
}) => {
  const precioNumero = Number(precioUnitario) || 0;
  const cantidadNumero = Number(cantidad) || 0;

  let precioNeto = precioNumero * (1 - (Number(descuento) || 0) / 100);
  if (cantidadNumero) {
    precioNeto -= (Number(descuentoCantidad) || 0) / cantidadNumero;
  }

  return margenDesdePrecio(precioNeto, costo, calcularPreciosSobre);
};

// Texto unico de las violaciones de margen. A proposito NO menciona el margen ni
// expone numeros: en la pantalla de captura ese porcentaje se confunde con el
// descuento y con el porcentaje de la lista, y el costo no es dato del vendedor.
export const MOTIVO_PRECIO_NO_ELEGIBLE =
  "Este precio no es elegible por las reglas de la lista de precios";

// Los descuentos se comparan con una centesima de tolerancia: tanto el descuento
// aplicado como el minimo exigible salen de round2Decimals, y sin holgura un
// redondeo de 0.01 marcaria como violacion una linea que el sistema mismo lleno.
const TOLERANCIA_PCT = 0.01;

/**
 * Descuento minimo que se le puede precargar a una linea sin romper el margen.
 *
 * El descuento minimo de la regla y el margen minimo pueden pelearse: un
 * descuento obligado del 10% puede hundir el margen por debajo de su piso. Aqui
 * MANDA EL MARGEN — se exige solo el descuento compatible — para que el sistema
 * nunca marque como violacion una linea que el mismo precargo.
 *
 * Se asume el renglon en modo porcentaje (el descuento en cantidad y el
 * porcentaje son excluyentes en la captura), por eso no entra descuentoCantidad.
 */
export const descuentoMinimoAplicable = ({
  regla,
  precioUnitario,
  cantidad,
  costo,
  calcularPreciosSobre,
}) => {
  if (!regla || !esNumero(regla.descuentoMinimo)) return undefined;

  let descuento = Number(regla.descuentoMinimo);

  const tope = descuentoMaximoAplicable(regla, cantidad);
  if (esNumero(tope)) descuento = Math.min(descuento, Number(tope));

  if (esNumero(regla.margenMinimo) && esNumero(costo) && esNumero(precioUnitario)) {
    const precioNetoMinimo = precioDesdeMargen(
      costo,
      regla.margenMinimo,
      calcularPreciosSobre
    );
    const precio = Number(precioUnitario);
    if (precioNetoMinimo !== null && precio > 0) {
      const maximoPorMargen = ((precio - precioNetoMinimo) / precio) * 100;
      descuento = Math.min(descuento, maximoPorMargen);
    }
  }

  return round2Decimals(Math.max(0, descuento));
};

export const validarLineaContraRegla = ({
  regla,
  precioUnitario,
  cantidad,
  descuento,
  descuentoCantidad,
  costo,
  calcularPreciosSobre,
}) => {
  if (!regla) return null;

  const margen = margenEfectivo({
    precioUnitario,
    cantidad,
    descuento,
    descuentoCantidad,
    costo,
    calcularPreciosSobre,
  });

  if (esNumero(regla.margenMinimo) && esNumero(margen) && margen < Number(regla.margenMinimo)) {
    return {
      campo: "precioUnitario",
      tipo: "margenMinimo",
      limite: round2Decimals(Number(regla.margenMinimo)),
      valor: round2Decimals(margen),
      motivo: MOTIVO_PRECIO_NO_ELEGIBLE,
    };
  }

  const topeDescuento = descuentoMaximoAplicable(regla, cantidad);
  const descuentoAplicado = descuentoEfectivo({
    precioUnitario,
    cantidad,
    descuento,
    descuentoCantidad,
  });
  if (esNumero(topeDescuento) && descuentoAplicado > Number(topeDescuento) + TOLERANCIA_PCT) {
    return {
      campo: "descuento",
      tipo: "descuentoMaximo",
      limite: round2Decimals(Number(topeDescuento)),
      valor: descuentoAplicado,
      motivo: `El descuento aplicado (${descuentoAplicado}%) excede el maximo permitido (${round2Decimals(Number(topeDescuento))}%)`,
    };
  }

  if (esNumero(regla.margenMaximo) && esNumero(margen) && margen > Number(regla.margenMaximo)) {
    return {
      campo: "precioUnitario",
      tipo: "margenMaximo",
      limite: round2Decimals(Number(regla.margenMaximo)),
      valor: round2Decimals(margen),
      motivo: MOTIVO_PRECIO_NO_ELEGIBLE,
    };
  }

  // Contra el minimo EXIGIBLE, no contra el crudo de la regla: si el margen
  // minimo no da para el descuento completo, exigirlo entero convertiria en
  // violacion la linea que el sistema acaba de precargar con el valor recortado.
  const minimoExigible = descuentoMinimoAplicable({
    regla,
    precioUnitario,
    cantidad,
    costo,
    calcularPreciosSobre,
  });
  if (esNumero(minimoExigible) && descuentoAplicado < Number(minimoExigible) - TOLERANCIA_PCT) {
    return {
      campo: "descuento",
      tipo: "descuentoMinimo",
      limite: round2Decimals(Number(minimoExigible)),
      valor: descuentoAplicado,
      motivo: `El descuento aplicado (${descuentoAplicado}%) es menor al minimo requerido (${round2Decimals(Number(minimoExigible))}%)`,
    };
  }

  return null;
};

const CAMPOS_SOBREESCRIBIBLES = [
  "porcentaje",
  "margenMinimo",
  "margenMaximo",
  "descuentoMinimo",
  "descuentoMaximo",
];

const aplicarCapa = (acumulado, regla, nivel) => {
  if (!regla) return;

  const camposAplicados = [];

  for (const campo of CAMPOS_SOBREESCRIBIBLES) {
    if (esNumero(regla[campo])) {
      acumulado[campo] = Number(regla[campo]);
      camposAplicados.push(campo);
    }
  }

  if (Array.isArray(regla.escalas) && regla.escalas.length) {
    acumulado.escalas = regla.escalas;
    camposAplicados.push("escalas");
  }

  if (camposAplicados.length) {
    acumulado.origen.push({
      nivel,
      reglaId: regla._id ? regla._id.toString() : null,
      campos: camposAplicados,
    });
  }
};

const reglasDelAlcance = ({ reglas, alcance, targetId, insumo }) => {
  const familiaId = insumo?.familiaId;
  const marcaId = insumo?.marcaId;

  const candidatas = (reglas || []).filter((regla) => {
    if (Number(regla.alcance) !== alcance) return false;
    if (regla.estado !== undefined && Number(regla.estado) !== 1) return false;

    if (alcance === ALCANCE.TIPO_CLIENTE && !mismoId(regla.tipoClienteId, targetId)) return false;
    if (alcance === ALCANCE.CLIENTE && !mismoId(regla.clienteId, targetId)) return false;

    if (regla.familiaId && !mismoId(regla.familiaId, familiaId)) return false;
    if (regla.marcaId && !mismoId(regla.marcaId, marcaId)) return false;

    return true;
  });

  const generales = candidatas.filter((r) => !r.familiaId && !r.marcaId);
  const porFamilia = candidatas.filter((r) => r.familiaId && !r.marcaId);
  const porMarca = candidatas.filter((r) => r.marcaId);

  return [...generales, ...porFamilia, ...porMarca];
};

export const resolverReglaPrecio = ({
  listaBase,
  reglas,
  insumo,
  clienteId,
  tipoClienteId,
  priorizarTipoCliente,
}) => {
  const acumulado = {
    precioId: listaBase?._id ? listaBase._id.toString() : null,
    nombre: listaBase?.nombre,
    porcentaje: esNumero(listaBase?.porcentaje) ? Number(listaBase.porcentaje) : undefined,
    margenMinimo: esNumero(listaBase?.margenMinimo) ? Number(listaBase.margenMinimo) : undefined,
    margenMaximo: esNumero(listaBase?.margenMaximo) ? Number(listaBase.margenMaximo) : undefined,
    descuentoMinimo: esNumero(listaBase?.descuentoMinimo)
      ? Number(listaBase.descuentoMinimo)
      : undefined,
    descuentoMaximo: esNumero(listaBase?.descuentoMaximo)
      ? Number(listaBase.descuentoMaximo)
      : undefined,
    redondeo: esNumero(listaBase?.redondeo) ? Number(listaBase.redondeo) : REDONDEO.NINGUNO,
    factorPrecioPiso: esNumero(listaBase?.factorPrecioPiso)
      ? Number(listaBase.factorPrecioPiso)
      : undefined,
    calcularAutomaticamente: listaBase?.calcularAutomaticamente,
    noBajarPrecios: listaBase?.noBajarPrecios,
    escalas: [],
    origen: [],
  };

  const capaLista = reglasDelAlcance({
    reglas,
    alcance: ALCANCE.LISTA,
    targetId: null,
    insumo,
  });
  for (const regla of capaLista) aplicarCapa(acumulado, regla, "lista");

  const capaTipo = tipoClienteId
    ? reglasDelAlcance({ reglas, alcance: ALCANCE.TIPO_CLIENTE, targetId: tipoClienteId, insumo })
    : [];
  const capaCliente = clienteId
    ? reglasDelAlcance({ reglas, alcance: ALCANCE.CLIENTE, targetId: clienteId, insumo })
    : [];

  const orden = priorizarTipoCliente
    ? [
        { reglas: capaCliente, nivel: "cliente" },
        { reglas: capaTipo, nivel: "tipoCliente" },
      ]
    : [
        { reglas: capaTipo, nivel: "tipoCliente" },
        { reglas: capaCliente, nivel: "cliente" },
      ];

  for (const capa of orden) {
    for (const regla of capa.reglas) aplicarCapa(acumulado, regla, capa.nivel);
  }

  return acumulado;
};

/**
 * Atajo para la captura de documentos: resuelve la regla del insumo y valida la
 * linea de una sola pasada. Regresa null si no hay nada que reportar.
 */
export const revisarLineaDeVenta = ({
  contexto,
  insumo,
  precioUnitario,
  cantidad,
  descuento,
  descuentoCantidad,
  costo,
}) => {
  if (!contexto || !contexto.listaBase) return null;

  const regla = resolverReglaPrecio({
    listaBase: contexto.listaBase,
    reglas: contexto.reglas,
    insumo,
    clienteId: contexto.clienteId,
    tipoClienteId: contexto.tipoClienteId,
    priorizarTipoCliente: contexto.priorizarTipoCliente,
  });

  return validarLineaContraRegla({
    regla,
    precioUnitario,
    cantidad,
    descuento,
    descuentoCantidad,
    costo,
    calcularPreciosSobre: contexto.calcularPreciosSobre,
  });
};
